import { useQuery, useQueryClient } from '@tanstack/react-query';
import { useState, type JSX } from 'react';
import { adminGenerationsApi } from '../api/admin-generations.js';
import { useI18n } from '../i18n/context.js';

/**
 * Состояние очереди и ретрай упавших задач (E16-T2).
 *
 * Ретрай — это `replay` без единой правки промпта: тот же путь, которым
 * пользуется песочница, поэтому повтор гарантированно уходит к провайдеру,
 * а не отдаёт кэш прежней попытки.
 */
export function AdminQueuePanel(): JSX.Element | null {
  const { t, formatDate } = useI18n();
  const queryClient = useQueryClient();
  const [message, setMessage] = useState<string | null>(null);
  const [retrying, setRetrying] = useState<string | null>(null);
  const queue = useQuery({
    queryKey: ['admin-queue'],
    queryFn: adminGenerationsApi.queue,
    refetchInterval: 5_000,
  });

  if (!queue.data) return null;

  const retry = (id: string): void => {
    setRetrying(id);
    setMessage(null);
    void adminGenerationsApi
      .retry(id)
      .then(() => {
        setMessage(t.admin.queue.retried);
        void queryClient.invalidateQueries({ queryKey: ['admin-queue'] });
        void queryClient.invalidateQueries({ queryKey: ['admin-generations'] });
      })
      .finally(() => setRetrying(null));
  };

  return (
    <section aria-labelledby="admin-queue" className="kitup-card mb-4 p-4">
      <h2 id="admin-queue" className="font-semibold">
        {t.admin.queue.title}
      </h2>

      <dl className="mt-3 flex flex-wrap gap-x-6 gap-y-2 text-sm">
        <Counter label={t.admin.queue.queued} value={queue.data.counts.queued} />
        <Counter label={t.admin.queue.running} value={queue.data.counts.running} />
        <Counter label={t.admin.queue.failed} value={queue.data.counts.failed} tone="error" />
        <Counter label={t.admin.queue.succeeded} value={queue.data.counts.succeeded} />
      </dl>

      {message ? (
        <p role="status" className="mt-3 text-sm text-[var(--kitup-color-muted)]">
          {message}
        </p>
      ) : null}

      {queue.data.failed.length === 0 ? (
        <p className="mt-3 text-sm text-[var(--kitup-color-muted)]">{t.admin.queue.empty}</p>
      ) : (
        <ul className="mt-3 grid gap-2">
          {queue.data.failed.map((item) => (
            <li
              key={item.id}
              className="flex flex-wrap items-center justify-between gap-3 border-t border-[var(--kitup-color-muted)]/15 pt-2 text-sm"
            >
              <span className="min-w-0">
                <span className="font-medium">
                  {t.admin.targets[item.targetType as keyof typeof t.admin.targets] ??
                    item.targetType}
                </span>
                <span className="text-[var(--kitup-color-muted)]">
                  {' · '}
                  {t.admin.queue.attempt(item.attempt)}
                  {item.finishedAt ? ` · ${formatDate(item.finishedAt)}` : ''}
                </span>
                {item.errorCode ? (
                  <span className="block truncate text-xs text-red-700">{item.errorCode}</span>
                ) : null}
              </span>
              <button
                type="button"
                className="kitup-secondary-button"
                disabled={retrying === item.id}
                onClick={() => retry(item.id)}
              >
                {t.admin.queue.retry}
              </button>
            </li>
          ))}
        </ul>
      )}
    </section>
  );
}

function Counter({
  label,
  value,
  tone,
}: {
  readonly label: string;
  readonly value: number;
  readonly tone?: 'error';
}): JSX.Element {
  const { locale } = useI18n();
  return (
    <div>
      <dt className="text-xs text-[var(--kitup-color-muted)]">{label}</dt>
      <dd
        className={`text-lg font-semibold ${tone === 'error' && value > 0 ? 'text-red-700' : ''}`}
      >
        {value.toLocaleString(locale)}
      </dd>
    </div>
  );
}
